import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * 그리드 셀 포커스/키보드 이동/드래그 범위 선택
 * @returns {Object} focusedCell, selectionRange, handlers, isCellFocused, isCellInSelection
 */
export function useGridCellFocus() {
  const [focusedCell, setFocusedCell] = useState({ rowId: null, field: null });
  const [selectionRange, setSelectionRange] = useState(null);
  const isDraggingRef = useRef(false);
  const anchorRef = useRef(null);

  const clearSelection = useCallback(() => setSelectionRange(null), []);

  /** (rowId, field)가 범위 안에 있는지 */
  const isCellInSelection = useCallback((rowId, field, rowIds, columnIds) => {
    if (!selectionRange || !rowIds?.length || !columnIds?.length) return false;
    const ri = rowIds.indexOf(rowId);
    const ci = columnIds.indexOf(field);
    if (ri < 0 || ci < 0) return false;
    const rMin = Math.min(
      rowIds.indexOf(selectionRange.startRowId),
      rowIds.indexOf(selectionRange.endRowId)
    );
    const rMax = Math.max(
      rowIds.indexOf(selectionRange.startRowId),
      rowIds.indexOf(selectionRange.endRowId)
    );
    const cMin = Math.min(
      columnIds.indexOf(selectionRange.startField),
      columnIds.indexOf(selectionRange.endField)
    );
    const cMax = Math.max(
      columnIds.indexOf(selectionRange.startField),
      columnIds.indexOf(selectionRange.endField)
    );
    return ri >= rMin && ri <= rMax && ci >= cMin && ci <= cMax;
  }, [selectionRange]);

  /**
   * 블럭 외곽 가이드용 CSS 변수 (가장자리 셀만 2px)
   * @returns {Record<string, string>|undefined}
   */
  const getSelectionEdgeVars = useCallback(
    (rowId, field, rowIds, columnIds) => {
      if (!selectionRange || !rowIds?.length || !columnIds?.length) return undefined;
      if (!isCellInSelection(rowId, field, rowIds, columnIds)) return undefined;
      const ri = rowIds.indexOf(rowId);
      const ci = columnIds.indexOf(field);
      if (ri < 0 || ci < 0) return undefined;
      const rMin = Math.min(
        rowIds.indexOf(selectionRange.startRowId),
        rowIds.indexOf(selectionRange.endRowId)
      );
      const rMax = Math.max(
        rowIds.indexOf(selectionRange.startRowId),
        rowIds.indexOf(selectionRange.endRowId)
      );
      const cMin = Math.min(
        columnIds.indexOf(selectionRange.startField),
        columnIds.indexOf(selectionRange.endField)
      );
      const cMax = Math.max(
        columnIds.indexOf(selectionRange.startField),
        columnIds.indexOf(selectionRange.endField)
      );
      return {
        '--sel-bt': ri === rMin ? '2px' : '0px',
        '--sel-bb': ri === rMax ? '2px' : '0px',
        '--sel-bl': ci === cMin ? '2px' : '0px',
        '--sel-br': ci === cMax ? '2px' : '0px',
      };
    },
    [selectionRange, isCellInSelection]
  );

  const isCellFocused = useCallback(
    (rowId, field) =>
      focusedCell?.rowId === rowId && focusedCell?.field === field,
    [focusedCell]
  );

  /** 좌/우만 이동 (편집 중 맨 앞·맨 끝에서 방향키 시 셀 이동용) */
  const moveFocusedCellHorizontal = useCallback(
    (direction, rows, columnIds, onSelectionChange) => {
      if (!rows?.length || !columnIds?.length) return;
      const current = focusedCell?.rowId != null
        ? { rowId: focusedCell.rowId, field: focusedCell.field }
        : { rowId: rows[0]?.id, field: columnIds[0] };
      let rowIdx = rows.findIndex((r) => r.id === current.rowId);
      let colIdx = columnIds.indexOf(current.field);
      if (rowIdx < 0) rowIdx = 0;
      if (colIdx < 0) colIdx = 0;
      const nextColIdx = direction === 'right' ? colIdx + 1 : colIdx - 1;
      let nextRowIdx = rowIdx;
      let nextCol = nextColIdx;
      if (nextCol < 0) {
        nextRowIdx = rowIdx - 1;
        nextCol = columnIds.length - 1;
      } else if (nextCol >= columnIds.length) {
        nextRowIdx = rowIdx + 1;
        nextCol = 0;
      }
      nextRowIdx = Math.max(0, Math.min(nextRowIdx, rows.length - 1));
      nextCol = Math.max(0, Math.min(nextCol, columnIds.length - 1));
      const nextRow = rows[nextRowIdx];
      const nextField = columnIds[nextCol];
      if (!nextRow || nextField == null) return;
      setFocusedCell({ rowId: nextRow.id, field: nextField });
      setSelectionRange(null);
      if (typeof onSelectionChange === 'function') {
        onSelectionChange(nextRow.index);
      }
    },
    [focusedCell]
  );

  /** 위/아래만 이동 (편집 중 Enter 시 아래 셀 이동용) */
  const moveFocusedCellVertical = useCallback(
    (direction, rows, columnIds, onSelectionChange) => {
      if (!rows?.length || !columnIds?.length) return;
      const current = focusedCell?.rowId != null
        ? { rowId: focusedCell.rowId, field: focusedCell.field }
        : { rowId: rows[0]?.id, field: columnIds[0] };
      let rowIdx = rows.findIndex((r) => r.id === current.rowId);
      const colIdx = columnIds.indexOf(current.field);
      if (rowIdx < 0) rowIdx = 0;
      const nextRowIdx = direction === 'down' ? rowIdx + 1 : rowIdx - 1;
      const clampedRowIdx = Math.max(0, Math.min(nextRowIdx, rows.length - 1));
      const nextRow = rows[clampedRowIdx];
      const nextField = columnIds[colIdx];
      if (!nextRow || nextField == null) return;
      setFocusedCell({ rowId: nextRow.id, field: nextField });
      setSelectionRange(null);
      if (typeof onSelectionChange === 'function') {
        onSelectionChange(nextRow.index);
      }
    },
    [focusedCell]
  );

  /** 방향키 / Tab으로 셀 이동 */
  const handleKeyDown = useCallback(
    (e, rows, columnIds, onSelectionChange) => {
      if (!rows?.length || !columnIds?.length) return;
      const current = focusedCell?.rowId != null
        ? { rowId: focusedCell.rowId, field: focusedCell.field }
        : { rowId: rows[0]?.id, field: columnIds[0] };
      let rowIdx = rows.findIndex((r) => r.id === current.rowId);
      let colIdx = columnIds.indexOf(current.field);
      if (rowIdx < 0) rowIdx = 0;
      if (colIdx < 0) colIdx = 0;

      let nextRowIdx = rowIdx;
      let nextColIdx = colIdx;
      switch (e.key) {
        case 'ArrowLeft':
          nextColIdx = colIdx - 1;
          break;
        case 'ArrowRight':
          nextColIdx = colIdx + 1;
          break;
        case 'ArrowUp':
          nextRowIdx = rowIdx - 1;
          break;
        case 'ArrowDown':
          nextRowIdx = rowIdx + 1;
          break;
        case 'Tab':
          if (e.shiftKey) {
            nextColIdx = colIdx - 1;
            if (nextColIdx < 0) {
              nextRowIdx = rowIdx - 1;
              nextColIdx = columnIds.length - 1;
            }
          } else {
            nextColIdx = colIdx + 1;
            if (nextColIdx >= columnIds.length) {
              nextRowIdx = rowIdx + 1;
              nextColIdx = 0;
            }
          }
          break;
        default:
          return;
      }

      e.preventDefault();
      nextRowIdx = Math.max(0, Math.min(nextRowIdx, rows.length - 1));
      nextColIdx = Math.max(0, Math.min(nextColIdx, columnIds.length - 1));
      const nextRow = rows[nextRowIdx];
      const nextField = columnIds[nextColIdx];
      if (!nextRow || nextField == null) return;
      setFocusedCell({ rowId: nextRow.id, field: nextField });
      setSelectionRange(null);
      if (typeof onSelectionChange === 'function') {
        onSelectionChange(nextRow.index);
      }
    },
    [focusedCell]
  );

  /** 셀 마우스다운: 포커스 + 드래그 시작 (우클릭은 블록 선택 유지) */
  const handleCellMouseDown = useCallback((e, rowId, field) => {
    // button 2 = 우클릭 — 선택 영역을 건드리지 않음 (집계/컨텍스트 메뉴용)
    if (e.button === 2) {
      setFocusedCell({ rowId, field });
      return;
    }
    setFocusedCell({ rowId, field });
    anchorRef.current = { rowId, field };
    setSelectionRange({ startRowId: rowId, startField: field, endRowId: rowId, endField: field });
    isDraggingRef.current = true;
  }, []);

  /** 셀 마우스진입: 드래그 중이면 범위 확장 */
  const handleCellMouseEnter = useCallback((rowId, field) => {
    if (!isDraggingRef.current || !anchorRef.current) return;
    setSelectionRange((prev) => {
      if (!prev) return { startRowId: anchorRef.current.rowId, startField: anchorRef.current.field, endRowId: rowId, endField: field };
      return { ...prev, endRowId: rowId, endField: field };
    });
  }, []);

  /** 전역 마우스업: 드래그 종료 */
  useEffect(() => {
    const onMouseUp = () => {
      isDraggingRef.current = false;
      anchorRef.current = null;
    };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  return {
    focusedCell,
    setFocusedCell,
    selectionRange,
    setSelectionRange,
    clearSelection,
    isCellFocused,
    isCellInSelection,
    getSelectionEdgeVars,
    handleKeyDown,
    handleCellMouseDown,
    handleCellMouseEnter,
    moveFocusedCellHorizontal,
    moveFocusedCellVertical,
  };
}
